import { zodResolver } from '@hookform/resolvers/zod';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';
import axios from 'axios';
import { useMemo, useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import { z } from 'zod';

import { paths } from '@/routes/paths';
import { Field, Form } from '@/shared/components/hook-form';
import { Iconify } from '@/shared/components/iconify';
import { EyeIcon, EyeOffIcon } from '@/shared/components/icons';
import { api } from '@/shared/lib/axios';

import { ActiveSessionsPanel } from './active-sessions-panel';

// Password rules mirror the design's checklist. Server side enforces a looser set
// (8+ chars in `api/src/features/auth/auth.dto.ts::passwordSchema`); the UI is
// intentionally stricter so the design's "Excellent" target is achievable.
const SYMBOL_REGEX = /[^A-Za-z0-9]/;
const newPasswordRules = z
  .string()
  .min(1, 'settings.account.changePassword.errors.newRequired')
  .refine((v) => v.trim() === v, 'settings.account.changePassword.errors.newEdgeSpaces')
  .refine((v) => v.length >= 12, 'settings.account.changePassword.errors.newTooShort')
  .refine(
    (v) => /[A-Z]/.test(v) && /[a-z]/.test(v) && /\d/.test(v) && SYMBOL_REGEX.test(v),
    'settings.account.changePassword.errors.newTooWeak',
  );

const schema = z
  .object({
    currentPassword: z.string().min(1, 'settings.account.changePassword.errors.currentRequired'),
    newPassword: newPasswordRules,
    confirmPassword: z.string().min(1, 'settings.account.changePassword.errors.mismatch'),
    signOutOthers: z.boolean(),
  })
  .refine((v) => v.newPassword === v.confirmPassword, {
    message: 'settings.account.changePassword.errors.mismatch',
    path: ['confirmPassword'],
  })
  .refine((v) => v.newPassword !== v.currentPassword, {
    message: 'settings.account.changePassword.errors.newSameAsCurrent',
    path: ['newPassword'],
  });

type FormValues = z.infer<typeof schema>;

interface ChangePasswordViewProps {
  onBack: () => void;
  isMobile?: boolean;
}

export function ChangePasswordView({ onBack, isMobile }: ChangePasswordViewProps) {
  const { t } = useTranslation('settings');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [submitErrorKey, setSubmitErrorKey] = useState<string | null>(null);
  const [successOpen, setSuccessOpen] = useState(false);

  const methods = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
      signOutOthers: true,
    },
    mode: 'onChange',
  });

  const {
    handleSubmit,
    reset,
    control,
    formState: { isSubmitting },
  } = methods;

  // Watch new+confirm so the strength meter, requirement checklist, and
  // "matches" indicator re-render as the user types. `signOutOthers` drives the
  // info banner — only shown when the toggle is on.
  const newPassword = useWatch({ control, name: 'newPassword' });
  const confirmPassword = useWatch({ control, name: 'confirmPassword' });
  const signOutOthers = useWatch({ control, name: 'signOutOthers' });

  const onSubmit = handleSubmit(async (values) => {
    setSubmitErrorKey(null);
    setSuccessOpen(false);
    try {
      await api.post('/api/auth/password/change', {
        currentPassword: values.currentPassword,
        newPassword: values.newPassword,
      });
      reset({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
        signOutOthers: values.signOutOthers,
      });
      setSuccessOpen(true);
    } catch (err) {
      if (!axios.isAxiosError(err)) {
        setSubmitErrorKey('settings.account.changePassword.errors.network');
        return;
      }
      const status = err.response?.status;
      const code = (err.response?.data as { error?: { code?: string } } | undefined)?.error?.code;
      if (status === 401 || code === 'INVALID_CREDENTIALS') {
        setSubmitErrorKey('settings.account.changePassword.errors.invalidCredentials');
      } else if (code === 'AUTH_METHOD_NOT_AVAILABLE') {
        setSubmitErrorKey('settings.account.changePassword.errors.noPassword');
      } else {
        setSubmitErrorKey('settings.account.changePassword.errors.network');
      }
    }
  });

  return (
    <Box>
      {/* Mobile drill-down already shows the title + back button in the
          dialog header — hide the inline breadcrumb to avoid duplication. */}
      {!isMobile && <Breadcrumb onBack={onBack} />}

      {signOutOthers && (
        <Alert
          icon={<Iconify icon="solar:danger-bold" width={16} />}
          severity="info"
          variant="outlined"
          sx={{
            mb: 3,
            borderRadius: (t) => `${t.shape.borderRadius}px`,
            backgroundColor: 'background.default',
            alignItems: 'center',
          }}
        >
          {t('account.changePassword.infoBanner')}
        </Alert>
      )}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          gap: { xs: 3, md: 4 },
          alignItems: 'start',
        }}
      >
        <Form methods={methods} onSubmit={onSubmit}>
          {submitErrorKey && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {t(submitErrorKey)}
            </Alert>
          )}
          {successOpen && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessOpen(false)}>
              {t('account.changePassword.success')}
            </Alert>
          )}

          <Stack spacing={2.5}>
            <Field.Text
              name="currentPassword"
              label={t('account.changePassword.currentLabel')}
              type={showCurrent ? 'text' : 'password'}
              autoComplete="current-password"
              variant="outlined"
              slotProps={{
                inputLabel: { shrink: true },
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowCurrent((v) => !v)}
                        edge="end"
                        aria-label={showCurrent ? 'Hide password' : 'Show password'}
                      >
                        {showCurrent ? <EyeIcon /> : <EyeOffIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              }}
            />

            <Box>
              <Field.Text
                name="newPassword"
                label={t('account.changePassword.newLabel')}
                type={showNew ? 'text' : 'password'}
                autoComplete="new-password"
                variant="outlined"
                slotProps={{
                  inputLabel: { shrink: true },
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                          {isNewPasswordValid(newPassword) && (
                            <Box sx={{ color: 'success.main', display: 'inline-flex' }}>
                              <Iconify icon="eva:checkmark-fill" width={20} />
                            </Box>
                          )}
                          <IconButton
                            onClick={() => setShowNew((v) => !v)}
                            edge="end"
                            aria-label={showNew ? 'Hide password' : 'Show password'}
                          >
                            {showNew ? <EyeIcon /> : <EyeOffIcon />}
                          </IconButton>
                        </Stack>
                      </InputAdornment>
                    ),
                  },
                }}
              />
              <StrengthMeter password={newPassword} />
              <RequirementsList password={newPassword} />
            </Box>

            <Field.Text
              name="confirmPassword"
              label={t('account.changePassword.confirmLabel')}
              type={showConfirm ? 'text' : 'password'}
              autoComplete="new-password"
              variant="outlined"
              slotProps={{
                inputLabel: { shrink: true },
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        {confirmPassword.length > 0 && confirmPassword === newPassword && (
                          <Box sx={{ color: 'success.main', display: 'inline-flex' }}>
                            <Iconify icon="eva:checkmark-fill" width={20} />
                          </Box>
                        )}
                        <IconButton
                          onClick={() => setShowConfirm((v) => !v)}
                          edge="end"
                          aria-label={showConfirm ? 'Hide password' : 'Show password'}
                        >
                          {showConfirm ? <EyeIcon /> : <EyeOffIcon />}
                        </IconButton>
                      </Stack>
                    </InputAdornment>
                  ),
                },
              }}
            />

            <Controller
              control={control}
              name="signOutOthers"
              render={({ field }) => (
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1.5}
                  sx={{
                    p: 1.75,
                    borderRadius: (t) => `${t.shape.borderRadius}px`,
                    border: 1,
                    borderColor: 'divider',
                  }}
                >
                  <Switch
                    checked={field.value}
                    onChange={(_e, checked) => field.onChange(checked)}
                  />
                  <Box>
                    <Typography sx={{ fontSize: 13.5, fontWeight: 500, color: 'text.primary' }}>
                      {t('account.changePassword.signOutOthers.label')}
                    </Typography>
                    <Typography sx={{ fontSize: 11.5, color: 'text.disabled' }}>
                      {t('account.changePassword.signOutOthers.hint')}
                    </Typography>
                  </Box>
                </Stack>
              )}
            />

            <Stack
              direction="row"
              alignItems="center"
              spacing={2}
              sx={{ flexWrap: 'wrap', gap: 1.5 }}
            >
              <Button
                type="submit"
                variant="contained"
                color="inherit"
                disabled={isSubmitting}
                endIcon={<Iconify icon="eva:arrow-forward-fill" width={16} />}
                sx={(theme) => ({
                  backgroundColor: theme.palette.text.primary,
                  color: theme.palette.background.default,
                  borderRadius: (t) => `${t.shape.borderRadius}px`,
                  px: 3,
                  py: 1.15,
                  fontWeight: 600,
                  boxShadow: 'none',
                  // Hover ramps the bg one step further from the page (toward
                  // black in light, toward lighter grey in dark) so the CTA
                  // stays high-contrast without inverting under dark mode.
                  '&:hover': {
                    backgroundColor:
                      theme.palette.mode === 'dark'
                        ? theme.palette.grey[400]
                        : theme.palette.grey[900],
                    boxShadow: 'none',
                  },
                })}
              >
                {isSubmitting
                  ? t('account.changePassword.submitting')
                  : t('account.changePassword.submit')}
              </Button>
              <Button
                type="button"
                variant="outlined"
                color="inherit"
                onClick={onBack}
                sx={{
                  borderRadius: (t) => `${t.shape.borderRadius}px`,
                  borderColor: 'divider',
                  color: 'text.primary',
                  px: 3,
                  py: 1.15,
                  fontWeight: 500,
                }}
              >
                {t('account.changePassword.cancel')}
              </Button>
              <Box sx={{ flex: 1 }} />
              <Link
                component={RouterLink}
                to={paths.auth.forgotPassword}
                sx={(theme) => ({
                  fontSize: 13,
                  fontWeight: 500,
                  color: theme.palette.primary.main,
                  textDecoration: 'none',
                  '&:hover': { textDecoration: 'underline' },
                })}
              >
                {t('account.changePassword.forgot')}
              </Link>
            </Stack>
          </Stack>
        </Form>

        <ActiveSessionsPanel />
      </Box>
    </Box>
  );
}

// ===========================================================================
// Breadcrumb header
// ===========================================================================

function Breadcrumb({ onBack }: { onBack: () => void }) {
  const { t } = useTranslation('settings');
  return (
    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
      <Link
        component="button"
        type="button"
        onClick={onBack}
        sx={{
          fontSize: 14,
          fontWeight: 500,
          color: 'text.secondary',
          textDecoration: 'none',
          '&:hover': { color: 'text.primary' },
        }}
      >
        {t('account.changePassword.breadcrumbAccount')}
      </Link>
      <Box
        sx={(theme) => ({
          color: 'text.disabled',
          display: 'inline-flex',
          transform: theme.direction === 'rtl' ? 'scaleX(-1)' : 'none',
        })}
      >
        <Iconify icon="eva:arrow-ios-forward-fill" width={14} />
      </Box>
      <Iconify icon="solar:settings-bold-duotone" width={20} />
      <Typography
        component="h2"
        sx={{
          fontFamily: '"Cairo Variable", sans-serif',
          fontSize: 18,
          fontWeight: 700,
          color: 'text.primary',
          letterSpacing: '-0.01em',
        }}
      >
        {t('account.changePassword.title')}
      </Typography>
    </Stack>
  );
}

// ===========================================================================
// Password strength + requirements
// ===========================================================================

interface RequirementChecks {
  length: boolean;
  uppercase: boolean;
  lowercase: boolean;
  number: boolean;
  symbol: boolean;
  noEdgeSpaces: boolean;
}

function evaluatePassword(password: string): RequirementChecks {
  return {
    length: password.length >= 12,
    uppercase: /[A-Z]/.test(password),
    lowercase: /[a-z]/.test(password),
    number: /\d/.test(password),
    symbol: SYMBOL_REGEX.test(password),
    noEdgeSpaces: password.length === 0 || password.trim() === password,
  };
}

function isNewPasswordValid(password: string): boolean {
  const r = evaluatePassword(password);
  return r.length && r.uppercase && r.lowercase && r.number && r.symbol && r.noEdgeSpaces;
}

function StrengthMeter({ password }: { password: string }) {
  const { t } = useTranslation('settings');
  const { score, label, entropy } = useMemo(() => scorePassword(password), [password]);

  if (!password) return null;

  const colors = ['#C77B5C', '#D7A05E', '#B5A04A', '#6B8E6F'];
  const segments = 4;

  return (
    <Box sx={{ mt: 1.5 }}>
      <Stack direction="row" spacing={0.5}>
        {Array.from({ length: segments }, (_, i) => (
          <Box
            key={i}
            sx={{
              flex: 1,
              height: 4,
              borderRadius: (t) => `${t.shape.borderRadius}px`,
              backgroundColor: i < score ? colors[score - 1] : 'action.hover',
              transition: 'background-color 0.2s',
            }}
          />
        ))}
      </Stack>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: 0.75 }}>
        <Typography
          sx={{
            fontSize: 12,
            fontWeight: 600,
            color: score >= 1 ? colors[score - 1] : 'text.disabled',
          }}
        >
          {t(`account.changePassword.strength.${label}`)}
        </Typography>
        <Typography sx={{ fontSize: 11.5, color: 'text.disabled' }}>
          {t('account.changePassword.strength.entropy', { bits: entropy })}
        </Typography>
      </Stack>
    </Box>
  );
}

function scorePassword(password: string): {
  score: 0 | 1 | 2 | 3 | 4;
  label: 'weak' | 'fair' | 'good' | 'excellent';
  entropy: number;
} {
  if (!password) return { score: 0, label: 'weak', entropy: 0 };
  const r = evaluatePassword(password);
  const classesUsed = [r.uppercase, r.lowercase, r.number, r.symbol].filter(Boolean).length;
  const charsetSize =
    (r.uppercase ? 26 : 0) + (r.lowercase ? 26 : 0) + (r.number ? 10 : 0) + (r.symbol ? 32 : 0) ||
    26;
  const entropy = Math.round(password.length * Math.log2(charsetSize));

  // Score 1 (weak) by default once non-empty; promote based on length + diversity.
  let score: 0 | 1 | 2 | 3 | 4 = 1;
  if (password.length >= 8 && classesUsed >= 2) score = 2;
  if (password.length >= 10 && classesUsed >= 3) score = 3;
  if (r.length && classesUsed >= 4 && r.noEdgeSpaces) score = 4;

  const labels = ['weak', 'fair', 'good', 'excellent'] as const;
  return { score, label: labels[score - 1] ?? 'weak', entropy };
}

function RequirementsList({ password }: { password: string }) {
  const { t } = useTranslation('settings');
  const checks = evaluatePassword(password);
  const rows: Array<{ key: keyof RequirementChecks; labelKey: string }> = [
    { key: 'length', labelKey: 'length' },
    { key: 'uppercase', labelKey: 'uppercase' },
    { key: 'lowercase', labelKey: 'lowercase' },
    { key: 'number', labelKey: 'number' },
    { key: 'symbol', labelKey: 'symbol' },
    { key: 'noEdgeSpaces', labelKey: 'noEdgeSpaces' },
  ];

  return (
    <Box
      sx={{
        mt: 1.5,
        p: 1.5,
        borderRadius: (t) => `${t.shape.borderRadius}px`,
        border: 1,
        borderColor: 'divider',
        backgroundColor: 'action.hover',
      }}
    >
      <Typography
        sx={{
          fontSize: 10.5,
          fontWeight: 700,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color: 'text.disabled',
          mb: 1,
        }}
      >
        {t('account.changePassword.requirements.title')}
      </Typography>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
          rowGap: 0.5,
          columnGap: 2,
        }}
      >
        {rows.map((row) => {
          const passed = checks[row.key];
          return (
            <Stack key={row.key} direction="row" alignItems="center" spacing={0.75}>
              <Box
                sx={{
                  width: 14,
                  height: 14,
                  borderRadius: '50%',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: passed ? 'success.main' : 'text.disabled',
                  flexShrink: 0,
                }}
              >
                <Iconify icon="eva:checkmark-fill" width={12} />
              </Box>
              <Typography
                sx={{
                  fontSize: 12.5,
                  color: passed ? 'text.primary' : 'text.secondary',
                }}
              >
                {t(`account.changePassword.requirements.${row.labelKey}`)}
              </Typography>
            </Stack>
          );
        })}
      </Box>
    </Box>
  );
}
